import { practiceBlock, type LearnerClient, type LearnerView } from "@tutor/live";
import type { FractionAnswer } from "@tutor/ui/widgets";
import type { HelpKind, LayoutMode, PracticeBlock } from "@tutor/protocol";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { helplineFor } from "../helpline";
import { useLocale, useT } from "../i18n";
import { InsightCards } from "../insights/InsightCards";
import type { VoiceAccess } from "../voice/access";
import { VoiceDock } from "../voice/VoiceDock";
import {
  answerFromWidget,
  widgetItem,
  WidgetActionsContext,
  type WidgetActions,
} from "../widgets/context";
import { BoardPage, PlanStrip, planIsToday } from "./Board";
import { Brouillon } from "./Brouillon";
import { ClassPicker } from "./ClassPicker";
import { ConnectionBanner } from "./ConnectionBanner";
import { Fin } from "./Fin";
import { HelpChips } from "./HelpChips";
import { ItemCard, itemLocked, lessonFirst, type Draft } from "./ItemCard";
import { Opener } from "./Opener";
import { MomentLayer, SkillSteps } from "./Progress";
import { useWideCanvas } from "./useWideCanvas";
import { Workbench } from "./Workbench";

// The lesson canvas. Its shape is `view.layout`, decided by the engine
// (`tutor-core::layout`) and published by the actor; the UI never picks it.
// - chat: conversation only, with a call to action to start practising;
// - focus: the item large, the tutor in a drawer (a bottom sheet on narrow screens);
// - split: item and conversation side by side (stacked on narrow screens);
// - review: compact cards, the tutor in the drawer.
// Coach is never out of sight (ADR-0044): on wide screens the drawer is a rail beside
// the exercise, always open; on narrow ones a peek bar shows Coach's latest line and
// opens the sheet. Help buttons under the exercise are offered by the engine, and a
// manipulative Coach sends sits on the workbench under the exercise when the engine says so.
// The conversation stays mounted across layouts, so a half-typed message survives.
// Two tabs share it (ADR-0042): "My exercise" and, while the parent's switch is on,
// "Free question". Each shows only its own messages.

// The conversation (assistant-ui, the OpenUI renderer) is its own chunk, so the exercise
// paints first. It starts loading with the first render of the canvas.
const ChatPanel = lazy(() =>
  import("../chat/ChatPanel").then((module) => ({ default: module.ChatPanel })),
);

const DRAWER_LAYOUTS: ReadonlySet<LayoutMode> = new Set<LayoutMode>(["focus", "review"]);

/**
 * How long "your next exercise is on its way" may show before the app asks for it. The next
 * item normally arrives with the grade (prefetch); it does not when the actor restarted
 * between the two (every deploy restarts actors), and nothing else would ever ask.
 */
export const NEXT_ITEM_GRACE_MS = 4_000;
/** How long the board waits for Coach's opening before offering the button again. */
export const OPEN_STALL_MS = 12_000;

// Blocks that may lift within the day (quiet hours end, the actor re-plans): the child
// can try again. The others hold until tomorrow or a parent's change, so no button.
const RETRYABLE: ReadonlySet<PracticeBlock> = new Set<PracticeBlock>([
  "quietHours",
  "askParent",
  "other",
]);

export function LessonScreen(props: {
  view: LearnerView;
  client: LearnerClient;
  onLock: () => void;
  /** Voice mode for this learner; `null` (or absent) when the household has no voice. */
  voice?: VoiceAccess | null;
}) {
  const { view, client, onLock, voice = null } = props;
  const t = useT();
  const locale = useLocale();
  // « On reprend » (feature 3) opens the session, in place of an exercise not started yet.
  const opening =
    view.opener !== null &&
    view.opener.sessionStart === view.plan?.sessionStart &&
    (view.item === null || view.item.done || view.item.attempts.length === 0);
  // « Fin » (feature 7): the child said « Je m'arrête », the session is closed, or the
  // parent's daily time is used up after some work.
  const todayPlan = planIsToday(view.plan) ? view.plan : null;
  const worked = Object.keys(view.climbs).length > 0;
  const [finishing, setFinishing] = useState(false);
  const [stayed, setStayed] = useState(false);
  const capped = worked && !stayed && practiceBlock(view) === "dailyCap";
  const closing =
    todayPlan !== null &&
    (finishing || view.ended?.sessionStart === todayPlan.sessionStart || capped);
  const layout = (opening || closing) && view.layout === "chat" ? "focus" : view.layout;
  // « Le tableau de Coach » (ADR-0063): one column, Coach's words then the exercise, the
  // composer at the bottom; no rail, no peek bar. The parent's switch, sent by the actor.
  const oneBoard = view.oneBoard;
  const wide = useWideCanvas();
  const drawer = DRAWER_LAYOUTS.has(layout) && !wide;
  const [talkOpen, setTalkOpen] = useState(false);
  const lastTutorId = lastTutorMessageId(view);
  const [seenTutorId, setSeenTutorId] = useState(lastTutorId);
  const talkVisible = !drawer || talkOpen;

  useEffect(() => {
    if (talkVisible) setSeenTutorId(lastTutorId);
  }, [talkVisible, lastTutorId]);

  // Coach's spoken replies are also written in the conversation: open the drawer when voice
  // starts, so the child sees them. Once per voice session; closing it again is respected.
  const voiceMode = view.mode === "voice";
  useEffect(() => {
    if (voiceMode) setTalkOpen(true);
  }, [voiceMode]);

  useEffect(() => {
    if (!drawer || !talkOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setTalkOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [drawer, talkOpen]);

  const item = opening ? null : view.item;
  // A board page open instead of the exercise (ADR-0052); a new session clears it.
  const [pageIndex, setPageIndex] = useState<number | null>(null);
  const openPage = pageIndex === null ? undefined : view.pages[pageIndex];
  const sessionStart = view.plan?.sessionStart ?? null;
  useEffect(() => {
    setPageIndex(null);
    setFinishing(false);
    setStayed(false);
  }, [sessionStart]);
  // The answer box's text, here rather than in the card so a FractionBars on the workbench
  // follows it and can write a tapped fraction back (both ways stay in sync). Per item: a
  // new exercise starts from what the child last sent for it, as the card always did.
  const [typed, setTyped] = useState<{ itemId: string; value: string } | null>(null);
  const draftItemId = item !== null && item.answer.kind !== "choice" ? item.itemId : null;
  const draftValue =
    item === null
      ? ""
      : typed?.itemId === item.itemId
        ? typed.value
        : (item.attempts.at(-1)?.answer ?? "");
  const draft = useMemo<Draft | undefined>(
    () =>
      draftItemId === null
        ? undefined
        : {
            value: draftValue,
            onChange(value) {
              setTyped({ itemId: draftItemId, value });
            },
          },
    [draftItemId, draftValue],
  );
  const workbenchAnswer = useMemo<FractionAnswer | null>(
    () =>
      draft === undefined || item === null || item.done || item.answer.kind !== "number"
        ? null
        : { ...draft, disabled: itemLocked(item) },
    [draft, item],
  );
  const referable = widgetItem(view);
  const widgetActions = useMemo<WidgetActions>(
    () => ({
      item: referable,
      answer(itemId, index) {
        answerFromWidget(client, referable, itemId, index);
      },
    }),
    [client, referable],
  );
  const startPractice = useCallback(() => {
    client.requestItem(locale);
  }, [client, locale]);
  const askHelp = useCallback(
    (kind: HelpKind) => {
      if (item === null) return;
      client.requestHelp(item.itemId, kind, locale);
      // Coach's answer comes in the conversation: show it.
      setTalkOpen(true);
    },
    [client, item, locale],
  );

  // The plan strip draws the climb of a goal's skill (« Deux feuilles »): no second climb.
  // The board's thin plan line has no goal bars (ADR-0063), so there the steps are drawn.
  const onPlan =
    !oneBoard && item !== null && todayPlan?.goals.some((g) => g.skillId === item.skillId) === true;

  const unread = drawer && !talkOpen && lastTutorId !== seenTutorId;
  const lastLine = drawer && !talkOpen ? lastTutorLine(view) : null;
  // A next step is always on screen (ADR-0044 point 9): the start button, or why practice
  // is closed and when it opens again; never a button that does nothing.
  const showCta = item === null || (item.done && view.upNext === null);
  const blocked = practiceBlock(view);

  // The finished exercise waits for its successor: ask once per wait, and again after a
  // reconnect. A refusal comes back as `practice.state` and the chat layout's explanation.
  const waitingFor =
    item?.done === true &&
    !closing &&
    view.upNext === null &&
    layout !== "chat" &&
    blocked === null &&
    view.status === "live"
      ? item.itemId
      : null;
  useEffect(() => {
    if (waitingFor === null) return;
    const timer = setTimeout(() => {
      client.requestItem(locale);
    }, NEXT_ITEM_GRACE_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [waitingFor, client, locale]);

  // On the board, Coach opens the session (ADR-0065): no empty conversation and no
  // « On s'entraîne » button to tap first. The app asks once per screen for what comes next;
  // the engine decides what that is (« On reprend », an exercise, or why practice is closed).
  // The ask is made again after a reconnect or once a block lifts (the key goes null in
  // between). If nothing comes, the board shows « On s'entraîne » again after a while, so
  // a lost or refused ask never leaves the child on « Je prépare » with no way on.
  const autoOpen = oneBoard && layout === "chat" && showCta && blocked === null && !closing;
  const openKey = autoOpen && view.status === "live" ? (item?.itemId ?? "start") : null;
  const askedFor = useRef<string | null>(null);
  const [stalled, setStalled] = useState<string | null>(null);
  useEffect(() => {
    if (openKey === null) {
      askedFor.current = null;
      setStalled(null);
      return;
    }
    if (askedFor.current !== openKey) {
      askedFor.current = openKey;
      client.requestItem(locale);
    }
    const timer = setTimeout(() => {
      setStalled(openKey);
    }, OPEN_STALL_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [openKey, client, locale]);
  const coachOpens = autoOpen && (openKey === null || stalled !== openKey);

  const planStrip = (
    <PlanStrip
      plan={view.plan}
      progress={view.progress}
      currentSkill={item?.done === false ? item.skillId : null}
      screenSkill={item?.skillId ?? null}
      pages={view.pages}
      page={openPage === undefined ? null : pageIndex}
      onPage={setPageIndex}
    />
  );
  const stageBody = closing ? (
    <Fin
      view={view}
      plan={todayPlan}
      client={client}
      onBack={() => {
        setFinishing(false);
        setStayed(true);
      }}
    />
  ) : (
    <>
      {!oneBoard && planStrip}
      {layout === "review" && <p className="stage-kicker">{t("lesson.reviewTitle")}</p>}
      {openPage !== undefined && pageIndex !== null && (
        <BoardPage
          page={openPage}
          index={pageIndex}
          onBack={() => {
            setPageIndex(null);
          }}
        />
      )}
      <div className="board-sheet" hidden={openPage !== undefined}>
        {item !== null && !onPlan && (
          <SkillSteps
            skillId={item.skillId}
            progress={view.progress[item.skillId]}
            fresh={
              view.moment?.milestone.kind === "stepReached" && view.moment.skillId === item.skillId
            }
          />
        )}
        <MomentLayer moment={view.moment} client={client} progress={view.progress} />
        {item !== null ? (
          <div className="sheets">
            <div className="sheet-main">
              <ItemCard
                key={item.itemId}
                item={item}
                upNext={view.upNext}
                client={client}
                compact={layout === "review"}
                {...(draft === undefined ? {} : { draft })}
              />
              {!lessonFirst(item) && (
                <>
                  <Workbench
                    workbench={view.workbench}
                    itemId={item.itemId}
                    done={item.done}
                    answer={workbenchAnswer}
                  />
                  <HelpChips
                    help={view.help}
                    itemId={item.itemId}
                    locked={itemLocked(item)}
                    onHelp={askHelp}
                  />
                </>
              )}
            </div>
            {!lessonFirst(item) && <Brouillon key={item.itemId} item={item} client={client} />}
          </div>
        ) : opening ? (
          <Opener
            key={view.opener.sessionStart}
            opener={view.opener}
            plan={view.plan}
            client={client}
            board={oneBoard}
          />
        ) : (
          <p className="stage-waiting" role="status">
            {t("lesson.preparing")}
          </p>
        )}
      </div>
      {todayPlan !== null && worked && openPage === undefined && (
        <button
          type="button"
          className="button ghost fin-open"
          onClick={() => {
            setFinishing(true);
          }}
        >
          {t("fin.open")}
        </button>
      )}
    </>
  );
  const cta = (
    <div className="cta">
      {showCta && blocked !== null && (
        <>
          <p className="cta-hint" role="status" data-blocked={blocked}>
            {t(`practice.blocked.${blocked}`, { helpline: helplineFor().number })}
          </p>
          {RETRYABLE.has(blocked) && (
            <button type="button" className="button ghost" onClick={startPractice}>
              {t("lesson.retryPractice")}
            </button>
          )}
        </>
      )}
      {showCta && blocked === null && (
        <>
          <ClassPicker classView={view.class} client={client} />
          <p className="cta-hint">
            {t(item === null ? "lesson.startPracticeHint" : "lesson.nextPracticeHint")}
          </p>
          <button type="button" className="button primary big" onClick={startPractice}>
            {t("lesson.startPractice")}
          </button>
        </>
      )}
      <InsightCards skills={view.skills} progress={view.progress} compact />
    </div>
  );
  const chat = (
    <ChatPanel
      messages={view.messages}
      status={view.status}
      client={client}
      ideas={view.curiosity && !coachOpens}
      opening={coachOpens}
      footer={
        oneBoard ? (
          <div className="board-flow">{layout === "chat" && !coachOpens ? cta : stageBody}</div>
        ) : layout === "chat" ? (
          cta
        ) : null
      }
    />
  );

  if (oneBoard) {
    return (
      <WidgetActionsContext.Provider value={widgetActions}>
        <div className="lesson one-board" data-layout={layout} data-mode={view.mode}>
          <ConnectionBanner status={view.status} queued={view.queued} onUnauthorized={onLock} />
          {voice !== null && (
            <VoiceDock
              access={voice}
              mode={view.mode}
              status={view.status}
              onUnauthorized={onLock}
            />
          )}
          {!closing && planStrip}
          <section className="board-column" aria-label={t("board.label")}>
            <Suspense fallback={<div className="chat chat-loading" aria-busy="true" />}>
              {chat}
            </Suspense>
          </section>
        </div>
      </WidgetActionsContext.Provider>
    );
  }

  return (
    <WidgetActionsContext.Provider value={widgetActions}>
      <div className="lesson" data-layout={layout} data-mode={view.mode}>
        <ConnectionBanner status={view.status} queued={view.queued} onUnauthorized={onLock} />

        {voice !== null && (
          <VoiceDock access={voice} mode={view.mode} status={view.status} onUnauthorized={onLock} />
        )}

        <section className="stage" hidden={layout === "chat"}>
          {stageBody}
        </section>

        <aside
          className="talk"
          data-open={talkVisible}
          inert={!talkVisible}
          aria-label={t("chat.label")}
        >
          {drawer && (
            <button
              type="button"
              className="button ghost talk-close"
              onClick={() => {
                setTalkOpen(false);
              }}
            >
              {t("lesson.closeTutor")}
            </button>
          )}
          <Suspense fallback={<div className="chat chat-loading" aria-busy="true" />}>
            <div className="talk-panel" id="talk-panel">
              {chat}
            </div>
          </Suspense>
        </aside>

        {drawer && !talkOpen && (
          <button
            type="button"
            className="talk-peek"
            aria-expanded={false}
            aria-label={t("lesson.openTutor")}
            onClick={() => {
              setTalkOpen(true);
            }}
          >
            <span className="talk-peek-who">{t("app.title")}</span>
            <span className="talk-peek-line" data-invite={lastLine === null}>
              {lastLine ?? t("lesson.peekInvite")}
            </span>
            {unread && (
              <span className="unread-dot" role="img" aria-label={t("lesson.newMessage")} />
            )}
          </button>
        )}
      </div>
    </WidgetActionsContext.Provider>
  );
}

/** Coach's latest words, streaming or complete, for the peek bar. */
function lastTutorLine(view: LearnerView): string | null {
  for (let i = view.messages.length - 1; i >= 0; i -= 1) {
    const entry = view.messages.at(i);
    if (entry?.kind !== "tutor") continue;
    const text = entry.text.trim();
    if (text !== "") return text;
  }
  return null;
}

/** The latest reply from Coach. */
function lastTutorMessageId(view: LearnerView): string | null {
  for (let i = view.messages.length - 1; i >= 0; i -= 1) {
    const entry = view.messages.at(i);
    if (entry?.kind === "tutor") return entry.id;
  }
  return null;
}
